import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ArabicFlag } from '../../assets/icons/flag-icons/arabic.svg';
import { ReactComponent as EnglishFlag } from '../../assets/icons/flag-icons/english.svg';
import { ReactComponent as FrenchFlag } from '../../assets/icons/flag-icons/french.svg';
import { ReactComponent as SpanishFlag } from '../../assets/icons/flag-icons/spanish.svg';
import { ReactComponent as GlobeIcon } from '../../assets/icons/globe-icon.svg';
import { ReactComponent as UpArrowIcon } from '../../assets/icons/up-arrow.svg';
// import MyButton from '../../shared-components/button/Button';
import { getCookie, setCookie } from '../../shared-functions/Cookies';

const languages = [
  { code: 'en', short: 'EN', lang: 'english', Icon: EnglishFlag },
  { code: 'ar', short: 'AR', lang: 'arabic', Icon: ArabicFlag },
  { code: 'fr', short: 'FR', lang: 'french', Icon: FrenchFlag },
  { code: 'sp', short: 'SP', lang: 'spanish', Icon: SpanishFlag },
];

interface LanguageSelectorProperties {
  isCollapsed: boolean;
  direction?: 'up' | 'down';
  allowedLanguages?: Array<{ language_name: string; language_code: string; _id: string }>;
}

/**
 * Language Selector Component
 * @param {LanguageSelectorProperties} properties - Component properties
 * @returns {any} The rendered component
 */
export default function LanguageSelector(properties: LanguageSelectorProperties) {
  const { isCollapsed = false, direction = 'up', allowedLanguages } = properties;
  const { t, i18n } = useTranslation('sharedComponent');
  const [open, setOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState('en');
  const selectorReference = useRef<HTMLDivElement>(null);

  // Load language from cookie or default to 'en'
  useEffect(() => {
    const savedLang = getCookie('i18next') || i18n.language || 'en';
    i18n.changeLanguage(savedLang);
    setCurrentLang(savedLang);
  }, [i18n]);

  /**
   * Change language and update cookie
   * @param {string} lng - Language code
   */
  const changeLanguage = (lng: string) => {
    if (currentLang === lng) return;

    // Debounce language change to prevent UI stutter from rapid clicks
    setOpen(false);
    setTimeout(() => {
      i18n.changeLanguage(lng);
      setCookie('i18next', lng, null, null);
      setCurrentLang(lng);
    }, 100);
  };

  const displayedLanguages =
    allowedLanguages && allowedLanguages.length > 0
      ? languages.filter((lang) =>
          allowedLanguages.some((allowed) => allowed.language_code === lang.code),
        )
      : languages;

  const currentLanguage =
    displayedLanguages.find((l) => l.code === currentLang) ||
    displayedLanguages[0] ||
    languages[0];

  useEffect(() => {
    const dirss = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.body.setAttribute('dir', dirss);
    document.documentElement.setAttribute('dir', dirss);
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    /**
     * Close the dropdown if clicked outside
     * @param {MouseEvent} event - The event
     */
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorReference.current && !selectorReference.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <div className="language-selector" ref={selectorReference}>
      {/* <MyButton
        label={headerLabel}
        buttonType="gray"
        onClickFunc={() => setOpen(!open)}
        svgIcon={<CurrentIcon style={{ width: 24, height: 16, borderRadius: 2 }} />}
        svgBackIcon={<UpArrowIcon />}
      /> */}
      <div
        className={`language-trigger ${isCollapsed ? 'collapsed' : ''} ${open ? 'open' : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => event.key === 'Enter' && setOpen(!open)}>
        <div className="left">
          <GlobeIcon className="globe-icon" />
          {!isCollapsed && <span className="label">{t('language')}</span>}
        </div>
        {!isCollapsed && (
          <div className="right">
            <currentLanguage.Icon className="selected-flag" />
            <span className="code">{currentLanguage.short}</span>
            <UpArrowIcon className={`arrow ${open ? 'open' : ''}`} />
          </div>
        )}
      </div>

      {open && (
        <div className={`dropdown-menu ${isCollapsed ? 'collapsed' : ''} direction-${direction}`}>
          {displayedLanguages.map((lng) => (
            <div
              key={lng.code}
              role="button"
              tabIndex={0}
              onClick={() => changeLanguage(lng.code)}
              onKeyDown={(even) => even.key === 'Enter' && changeLanguage(lng.code)}
              className={`menu-item ${lng.code === currentLang ? 'active' : ''}`}>
              <lng.Icon style={{ width: 24, height: 16, borderRadius: 2 }} />
              {t(lng.lang)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

LanguageSelector.defaultProps = {
  direction: 'up',
};
